import { useState, useEffect, useRef } from 'react';
import { supabase } from '../lib/supabase';

// Cualquier síntoma físico o médico, leve o grave — sin distinción de
// gravedad, ya que Vera nunca debe triar ni dar consejo clínico.
const ALARM_KEYWORDS = [
  'náusea', 'nausea', 'mareo', 'vómito', 'vomito', 'dolor', 'estreñimiento',
  'estreñida', 'diarrea', 'cansancio', 'cansada', 'cansado', 'fatiga',
  'reflujo', 'ardor', 'hinchazón', 'hinchazon', 'caída de pelo', 'caida de pelo',
  'no puedo comer', 'no puedo beber', 'dificultad respirar', 'desmayo',
  'urgencias', 'emergency', 'muy mal', 'sangre', 'fiebre', 'me duele'
];

function detectAlarm(text) {
  const lower = text.toLowerCase();
  return ALARM_KEYWORDS.some(kw => lower.includes(kw));
}

const ALARM_RESPONSE = 'Solo puedo ayudarte con tu bienestar general y con los retos sociales o emocionales que puedas tener mientras tomas GLP-1. Para cualquier tema médico, contacta con tu clínica o consulta nuestro canal de vídeos sobre síntomas frecuentes en GLP-1.';
const ALARM_RESPONSE_REPEATED = 'Veo que sigues con dudas sobre este tema — te abro nuestro canal de vídeos con información general sobre síntomas frecuentes en GLP-1, por si te resulta útil. Para tu caso concreto, contacta con tu clínica.';

// Detecta si la propia respuesta del modelo es la derivación médica plana
// (definida en el system prompt) — para contar la racha aunque el filtro de
// palabras clave del cliente no haya sido quien la disparó.
function isFlatDeflection(text) {
  return typeof text === 'string' && text.includes('bienestar general');
}

export function useChat(userId, options = {}) {
  const { onRepeatedMedicalQuestion } = options;
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [typing, setTyping] = useState(false);
  const alarmStreakRef = useRef(0);

  useEffect(() => {
    if (!userId) return;
    loadMessages();
  }, [userId]); // eslint-disable-line react-hooks/exhaustive-deps

  async function loadMessages() {
    const { data } = await supabase
      .from('chat_messages')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: true });
    if (data) setMessages(data);
  }

  async function sendMessage(text) {
    if (!text.trim()) return;

    const userMsg = {
      user_id: userId,
      role: 'user',
      content: text,
      flagged: detectAlarm(text),
    };

    const { data: saved } = await supabase
      .from('chat_messages')
      .insert(userMsg)
      .select()
      .single();

    const newMessages = [...messages, saved || { ...userMsg, id: Date.now(), created_at: new Date().toISOString() }];
    setMessages(newMessages);

    if (detectAlarm(text)) {
      alarmStreakRef.current += 1;
      const repeated = alarmStreakRef.current >= 2;
      const alarmMsg = {
        user_id: userId,
        role: 'assistant',
        content: repeated ? ALARM_RESPONSE_REPEATED : ALARM_RESPONSE,
        flagged: false,
      };
      const { data: savedAlarm } = await supabase
        .from('chat_messages')
        .insert(alarmMsg)
        .select()
        .single();
      setMessages([...newMessages, savedAlarm || { ...alarmMsg, id: Date.now() + 1, created_at: new Date().toISOString() }]);
      if (repeated) onRepeatedMedicalQuestion?.();
      return;
    }

    setTyping(true);
    setLoading(true);

    try {
      const history = newMessages.map(m => ({ role: m.role, content: m.content }));
      const { data: { session } } = await supabase.auth.getSession();

      // Llamada al proxy del servidor — la API key nunca toca el frontend
      const response = await fetch('/api/chat', {
        method: 'POST',
        headers: {
          'content-type': 'application/json',
          ...(session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {}),
        },
        body: JSON.stringify({ messages: history }),
      });

      if (!response.ok) {
        throw new Error(`Server error ${response.status}`);
      }

      const data = await response.json();
      const assistantText = data.text || 'Lo siento, no pude procesar tu mensaje.';
      const deflected = isFlatDeflection(assistantText);
      alarmStreakRef.current = deflected ? alarmStreakRef.current + 1 : 0;

      const assistantMsg = {
        user_id: userId,
        role: 'assistant',
        content: (deflected && alarmStreakRef.current >= 2) ? ALARM_RESPONSE_REPEATED : assistantText,
        flagged: false,
      };

      const { data: savedAssistant } = await supabase
        .from('chat_messages')
        .insert(assistantMsg)
        .select()
        .single();

      setMessages([...newMessages, savedAssistant || { ...assistantMsg, id: Date.now() + 1, created_at: new Date().toISOString() }]);
      if (deflected && alarmStreakRef.current >= 2) onRepeatedMedicalQuestion?.();
    } catch (err) {
      console.error('Error llamando a Vera:', err);
      setMessages([...newMessages, {
        id: Date.now() + 1,
        user_id: userId,
        role: 'assistant',
        content: 'Hubo un problema al conectar con Vera. Por favor inténtalo de nuevo.',
        flagged: false,
        created_at: new Date().toISOString(),
      }]);
    } finally {
      setTyping(false);
      setLoading(false);
    }
  }

  return { messages, loading, typing, sendMessage };
}
